import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { ShoppingCart, User, LogOut, Menu, X, Settings } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { getCartCount } = useCart();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [brands, setBrands] = useState([]);
  const isAdmin = user?.email === 'admin@justdive.co.za';

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      const res = await axios.get(`${API_URL}/brands`);
      setBrands(res.data || []);
    } catch (err) {
      console.error('Error fetching brands:', err);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  // Static nav links
  const staticLinks = [
    { to: '/membership', label: 'Membership' },
    { to: '/products', label: 'New Gear' },
    { to: '/pre-owned', label: 'Pre-Dived' },
    { to: '/courses', label: 'Courses' },
    { to: '/trips', label: 'Trips' },
    { to: '/events', label: 'Events' },
  ];

  // Add dynamic brand links
  const navLinks = [
    ...staticLinks,
    ...brands.map(brand => ({
      to: `/brand/${brand.slug}`,
      label: brand.name
    }))
  ];

  return (
    <nav className="bg-white/80 backdrop-blur-md border-b border-gray-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group" onClick={() => setMobileMenuOpen(false)}>
            <img 
              src="https://customer-assets.emergentagent.com/job_discount-tiers-1/artifacts/gus3g039_JustDive.co.za%20LOGO.png" 
              alt="Just Dive Logo" 
              className="h-12 sm:h-16 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link 
                key={link.to}
                to={link.to} 
                className="text-[#0A2342] hover:text-[#FF6B35] transition-colors font-medium"
                data-testid={`nav-${link.label.toLowerCase()}-link`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side - Cart, User, Mobile Menu */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Cart */}
            <Link to="/cart" className="relative" data-testid="nav-cart-link">
              <Button variant="ghost" size="icon" className="relative">
                <ShoppingCart className="w-5 h-5" strokeWidth={1.5} />
                {getCartCount() > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF6B35] text-white text-xs rounded-full flex items-center justify-center font-mono-accent">
                    {getCartCount()}
                  </span>
                )}
              </Button>
            </Link>

            {/* User actions - Desktop */}
            <div className="hidden md:flex items-center gap-2">
              {user ? (
                <>
                  {isAdmin && (
                    <Link to="/admin" data-testid="nav-admin-link">
                      <Button variant="ghost" size="icon" title="Admin Dashboard">
                        <Settings className="w-5 h-5" strokeWidth={1.5} />
                      </Button>
                    </Link>
                  )}
                  <Link to="/dashboard" data-testid="nav-dashboard-link">
                    <Button variant="ghost" size="icon">
                      <User className="w-5 h-5" strokeWidth={1.5} />
                    </Button>
                  </Link>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    onClick={handleLogout}
                    data-testid="nav-logout-btn"
                  >
                    <LogOut className="w-5 h-5" strokeWidth={1.5} />
                  </Button>
                </>
              ) : (
                <Link to="/auth" data-testid="nav-login-link">
                  <Button 
                    className="bg-[#0A2342] text-white hover:bg-[#0A2342]/90 shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    Sign In
                  </Button>
                </Link>
              )}
            </div>

            {/* Mobile Menu Button */}
            <Button 
              variant="ghost" 
              size="icon" 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" strokeWidth={1.5} />
              ) : (
                <Menu className="w-6 h-6" strokeWidth={1.5} />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="block px-4 py-3 text-[#0A2342] hover:bg-[#F0EFEA] hover:text-[#FF6B35] rounded-lg font-medium transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            
            <div className="border-t border-gray-200 my-3 pt-3">
              {user ? (
                <>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="block px-4 py-3 text-[#FF6B35] hover:bg-orange-50 rounded-lg font-medium transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Admin Dashboard
                    </Link>
                  )}
                  <Link
                    to="/dashboard"
                    className="block px-4 py-3 text-[#0A2342] hover:bg-[#F0EFEA] hover:text-[#FF6B35] rounded-lg font-medium transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    My Account
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg font-medium transition-colors"
                  >
                    Sign Out
                  </button>
                </>
              ) : (
                <Link
                  to="/auth"
                  className="block px-4 py-3 bg-[#0A2342] text-white text-center rounded-lg font-medium"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
